import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { PdfDocument } from './pdf-document';
export { PdfDocument };
import { pdfDocumentSchema } from '@schemas/business-objects/pdf-document';
import { ResourceFormGroup } from './resource-form-group';
import { TestArticleFormGroup } from './test-article-form-group';
import type { TextBox } from './text-box';
import type { User } from './user';

export class PdfDocumentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    fileName: SchemaFormControl;
    mimeType: SchemaFormControl;
    size: SchemaFormControl;
    base64Content: SchemaFormControl;
    locked: SchemaFormControl;
    resourceId: SchemaFormControl;
    resource: ResourceFormGroup;
    testArticleId: SchemaFormControl;
    testArticle: TestArticleFormGroup;
  };

  declare value: Partial<PdfDocument>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(pdfDocumentSchema, PdfDocument, data, options);
  }

  get object(): PdfDocument {
    return new PdfDocument(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'resource':
        return new ResourceFormGroup();
      case 'testArticle':
        return new TestArticleFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<PdfDocument[]> {
    return PdfDocument.find(filter);
  }

  static async findOne(filter?: any): Promise<PdfDocument | null> {
    return PdfDocument.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<PdfDocument> {
    return PdfDocument.findById(id, filter);
  }

  static async create(data: Partial<PdfDocument>): Promise<PdfDocument> {
    return PdfDocument.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<PdfDocument>,
  ): Promise<PdfDocument> {
    return PdfDocument.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return PdfDocument.deleteById(id);
  }

  async dataForCopy(): Promise<any> {
    const instance = new PdfDocument(this.getRawValue());
    return instance.dataForCopy();
  }

  async content(): Promise<any> {
    const instance = new PdfDocument(this.getRawValue());
    return instance.content();
  }
}

export class PdfDocumentFormArray extends PersistedFormArray {
  readonly entityName = 'PdfDocument' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(pdfDocumentSchema, PdfDocument, options);
  }

  protected override createItemGroup() {
    return new PdfDocumentFormGroup();
  }

  get array(): PdfDocument[] {
    return this.controls.map((group: PdfDocumentFormGroup) => group.object);
  }
}

export class PdfDocumentPersistedArray extends PersistedArray<PdfDocument> {
  readonly entityName = 'PdfDocument' as const;

  constructor(options?: PersistedArrayOptions) {
    super(PdfDocument, options);
  }
}
